import {
  CLEO_MARK_CORE,
  CLEO_MARK_MEMBRANE_PATH,
  CLEO_MARK_MEMBRANE_STROKE_WIDTH,
  CLEO_MARK_SLICE_END_OPACITY,
  CLEO_MARK_SLICE_PATH,
  CLEO_MARK_VIEW_BOX,
} from "@cleo/shared/cleoMarkGeometry";
import { useId, type SVGProps } from "react";

export interface CleoMarkProps extends Omit<SVGProps<SVGSVGElement>, "color"> {
  readonly color?: string;
  readonly coreColor?: string;
  readonly decorative?: boolean;
  readonly label?: string;
  readonly showSession?: boolean;
}

/**
 * Pure Cleo geometry. This renderer carries no presence or authority state;
 * callers must supply any earned semantic treatment explicitly.
 */
export function CleoMark({
  color = "currentColor",
  coreColor = color,
  decorative = false,
  label = "Cleo",
  showSession = false,
  ...svgProps
}: CleoMarkProps) {
  const gradientId = `${useId().replaceAll(":", "")}-cleo-slice`;

  return (
    <svg
      {...svgProps}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : label}
      fill="none"
      focusable="false"
      role={decorative ? undefined : "img"}
      viewBox={CLEO_MARK_VIEW_BOX}
    >
      {showSession ? (
        <>
          <defs>
            <radialGradient id={gradientId} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={color} stopOpacity={0} />
              <stop offset="100%" stopColor={color} stopOpacity={CLEO_MARK_SLICE_END_OPACITY} />
            </radialGradient>
          </defs>
          <path d={CLEO_MARK_SLICE_PATH} fill={`url(#${gradientId})`} />
        </>
      ) : null}
      <path
        d={CLEO_MARK_MEMBRANE_PATH}
        stroke={color}
        strokeLinecap="round"
        strokeWidth={CLEO_MARK_MEMBRANE_STROKE_WIDTH}
      />
      <circle cx={CLEO_MARK_CORE.cx} cy={CLEO_MARK_CORE.cy} fill={coreColor} r={CLEO_MARK_CORE.r} />
    </svg>
  );
}
